import React from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { ShieldCheck, Mail } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useSettings } from "../context/SettingsContext";

const sections = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    body: `We may collect personal information you provide directly to us, such as your name, email address, phone number, and company details when you fill out a contact form, register for training, or apply for a role through our site. We also automatically collect certain technical information, including your IP address, browser type, device information, and pages visited, through cookies and similar technologies.`,
  },
  {
    id: "how-we-use-it",
    title: "2. How We Use Your Information",
    body: `We use the information we collect to respond to inquiries, provide our consulting, training, and staffing services, process applications and enrolments, improve our website and offerings, send relevant updates where you've opted in, and comply with legal obligations.`,
  },
  {
    id: "cookies",
    title: "3. Cookies & Tracking Technologies",
    body: `Our website uses cookies and similar technologies to remember your preferences, understand how visitors use our site, and improve performance. You can control or disable cookies through your browser settings; doing so may affect certain features of the site.`,
  },
  {
    id: "sharing",
    title: "4. Sharing of Information",
    body: `We do not sell your personal information. We may share information with trusted service providers who help us operate our business (such as hosting or analytics providers), with corporate or hiring partners when relevant to a service you've requested, or when required by law or to protect our legal rights.`,
  },
  {
    id: "security",
    title: "5. Data Security",
    body: `We implement reasonable administrative, technical, and physical safeguards designed to protect your personal information. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.`,
  },
  {
    id: "retention",
    title: "6. Data Retention",
    body: `We retain personal information for as long as necessary to fulfil the purposes described in this policy, unless a longer retention period is required or permitted by law.`,
  },
  {
    id: "your-rights",
    title: "7. Your Rights",
    body: `Depending on your location, you may have the right to access, correct, update, or request deletion of your personal information. To exercise these rights, please contact us using the details below.`,
  },
  {
    id: "third-party-links",
    title: "8. Third-Party Links",
    body: `Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those third-party sites, and we encourage you to review their privacy policies.`,
  },
  {
    id: "childrens-privacy",
    title: "9. Children's Privacy",
    body: `Our services are not directed to individuals under the age of 18, and we do not knowingly collect personal information from children.`,
  },
  {
    id: "changes",
    title: "10. Changes to This Policy",
    body: `We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.`,
  },
];

// Justified paragraph — even left-and-right edges for a formal, legal-document feel.
function JustifiedText({ children }) {
  return (
    <Typography sx={{ fontSize: 14.5, lineHeight: 1.95, color: "text.secondary", textAlign: "justify", textJustify: "inter-word" }}>
      {children}
    </Typography>
  );
}

export default function PrivacyPolicy() {
  const settings = useSettings();
  const companyName = settings.site_name || "NetLabs IT Consultancy";
  const email = settings.company_email || "info@netlabsits.com";
  const effectiveDate = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });

  return (
    <Box>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 15, md: 19 },
          pb: { xs: 8, md: 10 },
          background: "linear-gradient(180deg, #0B1220 0%, #101A2E 60%, #0F172A 100%)",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -160,
            left: "50%",
            transform: "translateX(-50%)",
            width: 560,
            height: 560,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37,99,235,0.32) 0%, transparent 68%)",
          }}
        />
        <Box sx={{ position: "relative", maxWidth: 860, mx: "auto", px: { xs: 2, md: 4 }, textAlign: "center" }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "16px",
              bgcolor: "rgba(147,197,253,0.12)",
              border: "1px solid rgba(147,197,253,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <ShieldCheck size={24} color="#93C5FD" strokeWidth={2} />
          </Box>
          <Typography sx={{ fontSize: { xs: 30, md: 42 }, fontWeight: 800, color: "white", letterSpacing: "-0.02em", mb: 2 }}>
            Privacy Policy
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: "rgba(226,232,240,0.7)" }}>
            Effective date: {effectiveDate}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ py: { xs: 8, md: 11 } }}>
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 5, md: 8 } }}>
            {/* Table of contents rail */}
            <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}>
              <Box sx={{ position: { md: "sticky" }, top: 110 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}>
                  On This Page
                </Typography>
                <Stack spacing={1.1}>
                  {sections.map((s) => (
                    <Box
                      key={s.id}
                      component="a"
                      href={`#${s.id}`}
                      sx={{
                        fontSize: 13.5,
                        color: "text.secondary",
                        textDecoration: "none",
                        display: "block",
                        py: 0.25,
                        borderLeft: "2px solid transparent",
                        pl: 1.5,
                        "&:hover": { color: "#2563EB", borderLeftColor: "#2563EB" },
                        transition: "color 0.15s ease, border-color 0.15s ease",
                      }}
                    >
                      {s.title}
                    </Box>
                  ))}
                  <Box
                    component="a"
                    href="#contact-us"
                    sx={{
                      fontSize: 13.5,
                      color: "text.secondary",
                      textDecoration: "none",
                      display: "block",
                      py: 0.25,
                      borderLeft: "2px solid transparent",
                      pl: 1.5,
                      "&:hover": { color: "#2563EB", borderLeftColor: "#2563EB" },
                      transition: "color 0.15s ease, border-color 0.15s ease",
                    }}
                  >
                    11. Contact Us
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 760 }}>
              <Box
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  mb: 5,
                  borderRadius: "16px",
                  bgcolor: "#F8FAFC",
                  border: "1px solid rgba(15,23,42,0.08)",
                }}
              >
                <JustifiedText>
                  {companyName} ("we", "us", or "our") respects your privacy and is committed to protecting the
                  personal information you share with us. This Privacy Policy explains how we collect, use,
                  disclose, and safeguard your information when you visit our website or use our services.
                </JustifiedText>
              </Box>

              <Stack divider={<Divider sx={{ my: 4.5 }} />} spacing={0}>
                {sections.map(({ id, title, body }) => (
                  <Box key={id} id={id} sx={{ scrollMarginTop: "110px" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0F172A", mb: 1.5 }}>{title}</Typography>
                    <JustifiedText>{body}</JustifiedText>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 4.5 }} />

              <Box id="contact-us" sx={{ scrollMarginTop: "110px" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0F172A", mb: 1.5 }}>11. Contact Us</Typography>
                <JustifiedText>
                  If you have any questions about this Privacy Policy or how we handle your information, please
                  contact us using the details below.
                </JustifiedText>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "rgba(37,99,235,0.06)",
                    border: "1px solid rgba(37,99,235,0.15)",
                    width: "fit-content",
                  }}
                >
                  <Mail size={17} color="#2563EB" />
                  <Box
                    component="a"
                    href={`mailto:${email}`}
                    sx={{ fontSize: 14, fontWeight: 700, color: "#2563EB", textDecoration: "none" }}
                  >
                    {email}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}